import { ScrollReveal } from "@/components/ScrollReveal";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ArrowRight, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";
import { scrollToSection } from "@/lib/scrollToSection";
import { AgentPipeline, AgentPipelineMobile } from "@/components/AgentPipeline";
import AICapabilityCards from "@/components/AICapabilityCards";
import ScrollCue from "@/components/ScrollCue";
import { subscribeScroll, prefersReducedMotion } from "@/lib/scrollEngine";



const Heading = () => {
  const { t } = useTranslation();
  return (
    <div className="text-center max-w-3xl mx-auto">
      <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold leading-tight mb-3">
        {t("ai.headingLine1")}
        <br />
        <span className="gradient-text">{t("ai.headingLine2")}</span>
      </h2>
      <p className="text-sm md:text-base text-muted-foreground max-w-2xl mx-auto">
        {t("ai.subtitle")}
      </p>
    </div>
  );
};

const AISection = () => {
  const { t } = useTranslation();
  const outerRef = useRef<HTMLDivElement>(null);
  const [p, setP] = useState(0);

  useEffect(() => {
    const el = outerRef.current;
    if (!el) return;
    if (prefersReducedMotion()) {
      setP(1);
      return;
    }
    return subscribeScroll(() => {
      const rect = el.getBoundingClientRect();
      const total = rect.height - window.innerHeight;
      const scrolled = Math.min(Math.max(-rect.top, 0), total);
      setP(total > 0 ? scrolled / total : 0);
    });
  }, []);

  

  return (
    <>
      {/* DESKTOP — pinned scroll-driven agent pipeline */}
      <section ref={outerRef} className="relative hidden md:block bg-card text-foreground border-y border-border" style={{ height: "340vh" }}>
        <div className="sticky top-0 h-[100dvh] flex flex-col justify-center overflow-hidden pt-28">
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="aurora-blob aurora-1 w-[55%] h-[55%] -top-[10%] -left-[5%]" />
            <div className="aurora-blob aurora-2 w-[50%] h-[50%] top-[20%] right-[-8%]" />
            <div className="aurora-blob aurora-3 w-[45%] h-[45%] bottom-[-12%] left-[30%]" />
          </div>
          <div className="absolute inset-0 tech-grid opacity-[0.1] pointer-events-none" />

          <div className="container relative z-10 flex-1 min-h-0 flex flex-col justify-center py-4 lg:py-6">
            <div className="mb-3 lg:mb-5 shrink-0">
              <Heading />
            </div>

            {/* Pipeline canvas — box matches flow aspect so headline/footer hug it (no internal whitespace) */}
            <div className="w-full max-w-[1600px] aspect-[1480/600] max-h-[55dvh] mx-auto">
              <AgentPipeline p={p} />
            </div>
          </div>
          <ScrollCue progress={p} />
        </div>
      </section>

      {/* MOBILE — vertical stacked pipeline */}
      <section className="md:hidden relative bg-card text-foreground border-t border-border py-16 overflow-hidden">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="aurora-blob aurora-1 w-[70%] h-[40%] -top-[5%] -left-[10%]" />
          <div className="aurora-blob aurora-2 w-[60%] h-[40%] bottom-[5%] right-[-10%]" />
        </div>
        <div className="absolute inset-0 tech-grid opacity-[0.08] pointer-events-none" />
        <div className="container relative z-10">
          <div className="mb-10">
            <Heading />
          </div>
          <AgentPipelineMobile />
        </div>
      </section>

      {/* Capability stats + CTA */}
      <section className="section-y bg-background text-foreground border-b border-border relative overflow-hidden">
        <div className="absolute inset-0 tech-grid opacity-[0.08] pointer-events-none" />
        <div className="container relative z-10">
          <AICapabilityCards />


          <ScrollReveal delay={300} className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-5">
            <span className="hover-glow-accent block w-full sm:inline-block sm:w-auto">
              <a
                href="#blueprint"
                onClick={(e) => { e.preventDefault(); scrollToSection("blueprint"); }}
                className="inline-flex w-full sm:w-auto justify-center items-center gap-3 px-8 py-4 btn-clip bg-primary text-primary-foreground btn-label-contrast font-medium text-lg transition-transform duration-200 active:scale-[0.97] group"
              >
                <Sparkles size={18} />
                {t("ai.ctaPrimary")}
                <ArrowRight size={20} className="transition-transform duration-300 group-hover:translate-x-1" />
              </a>
            </span>
            <span className="hover-glow-accent block w-full sm:inline-block sm:w-auto">
              <Link
                to="/contact"
                className="inline-flex w-full sm:w-auto justify-center items-center gap-3 px-8 py-4 btn-clip bg-card/40 text-foreground font-medium text-lg transition-transform duration-200 active:scale-[0.97] group"
              >
                {t("ai.ctaSecondary")}
                <ArrowRight size={20} className="transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </span>
          </ScrollReveal>

        </div>
      </section>
    </>
  );
};

export default AISection;
